import { useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Temporal } from 'temporal-polyfill';
import { buildMonthGrid, dayLabel, formatDayStamp, formatYearMonth } from '../domain/calendar';
import { groupByDate } from '../domain/schedule';
import { formatDuration, formatTime, weekdayShort } from '../domain/format';
import type { Location, Occurrence, Weekday } from '../domain/types';
import { useSchedule } from './useSchedule';
import { EmptyState, ErrorBanner } from './components';
import { ChevronRight } from './icons';
import { OccurrenceSheet, wallClock } from './OccurrenceSheet';
import { SHOW_EMPTY_DAYS_KEY, usePreference } from './usePreference';

/** How far ahead the list looks. Long enough to plan, short to keep scrolling sane. */
const LIST_DAYS = 30;

type Mode = 'list' | 'month';

export function ScheduleView(props: {
  onAddClass: () => void;
  onEditSeries: (seriesId: string) => void;
  /** Overrides the month shown on first render. Defaults to the current month. */
  initialMonth?: Temporal.PlainYearMonth;
  /** Overrides "today". Tests inject this instead of stubbing timers. */
  today?: Temporal.PlainDate;
}): JSX.Element {
  const today = useMemo(() => props.today ?? Temporal.Now.plainDateISO(), [props.today]);
  const [mode, setMode] = useState<Mode>('list');

  return (
    <section>
      <header class="screen-head">
        <h2>Schedule</h2>
      </header>

      <div class="segmented" role="group" aria-label="View">
        <button
          type="button"
          class={`segment${mode === 'list' ? ' selected' : ''}`}
          aria-pressed={mode === 'list'}
          onClick={() => setMode('list')}
        >
          List
        </button>
        <button
          type="button"
          class={`segment${mode === 'month' ? ' selected' : ''}`}
          aria-pressed={mode === 'month'}
          onClick={() => setMode('month')}
        >
          Month
        </button>
      </div>

      {mode === 'list' ? (
        <ListSchedule
          today={today}
          onAddClass={props.onAddClass}
          onEditSeries={props.onEditSeries}
        />
      ) : (
        <MonthSchedule
          today={today}
          initialMonth={props.initialMonth ?? today.toPlainYearMonth()}
          onEditSeries={props.onEditSeries}
        />
      )}
    </section>
  );
}

/** Shared selection + sheet wiring for both views. */
function useSelection(occurrences: readonly Occurrence[]) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = occurrences.find((item) => String(item.key) === selectedKey);
  return { selectedKey, setSelectedKey, selected };
}

function ListSchedule(props: {
  today: Temporal.PlainDate;
  onAddClass: () => void;
  onEditSeries: (seriesId: string) => void;
}): JSX.Element {
  const rangeEnd = useMemo(() => props.today.add({ days: LIST_DAYS }), [props.today]);
  const schedule = useSchedule(props.today, rangeEnd);
  const occurrences = schedule.data?.occurrences ?? [];
  const { selectedKey, setSelectedKey, selected } = useSelection(occurrences);
  const [showEmptyDays, setShowEmptyDays] = usePreference(SHOW_EMPTY_DAYS_KEY, false);

  const byDate = useMemo(() => groupByDate(occurrences), [occurrences]);
  const colorBySeries = useMemo(
    () => new Map((schedule.data?.series ?? []).map((item) => [item.id, item.color])),
    [schedule.data?.series],
  );
  const hasAnyClasses = (schedule.data?.series.length ?? 0) > 0;

  // Days are listed from today so the answer to "what's next" is at the top.
  const days = useMemo(() => {
    const result: Temporal.PlainDate[] = [];
    for (let index = 0; index <= LIST_DAYS; index += 1) {
      result.push(props.today.add({ days: index }));
    }
    return result;
  }, [props.today]);

  // Trailing empty days would be a wall of "Nothing scheduled", so stop after the
  // last day that actually has something.
  const lastBusyIndex = days.reduce(
    (last, date, index) => (byDate.has(date.toString()) ? index : last),
    -1,
  );
  const bounded = lastBusyIndex === -1 ? days.slice(0, 3) : days.slice(0, lastBusyIndex + 1);
  const visibleDays = showEmptyDays
    ? bounded
    : bounded.filter((date) => byDate.has(date.toString()));

  return (
    <>
      <ErrorBanner error={schedule.error} />
      <UnresolvedWarning count={schedule.data?.unresolvedSeriesIds.length ?? 0} />

      {!schedule.loading && !hasAnyClasses ? (
        <EmptyState title="Nothing scheduled yet.">
          <p>Add the classes you teach and they'll appear here.</p>
          <button type="button" class="primary" onClick={props.onAddClass}>
            Add a class
          </button>
        </EmptyState>
      ) : null}

      {/* Classes exist but none land in the visible window (all ended or
          archived): a blank list with no way forward would be a dead end. */}
      {!schedule.loading && hasAnyClasses && occurrences.length === 0 ? (
        <EmptyState title="Nothing coming up.">
          <p>None of your classes have sessions in the next month.</p>
          <button type="button" class="primary" onClick={props.onAddClass}>
            Add a class
          </button>
        </EmptyState>
      ) : null}

      {hasAnyClasses && occurrences.length > 0 ? (
        <>
          {/* Kept on the list itself: it only affects this view, so putting it
              behind a settings screen would hide it from the one place it
              matters. */}
          <label class="list-option">
            <span>Show free days</span>
            <input
              type="checkbox"
              role="switch"
              class="switch"
              checked={showEmptyDays}
              onChange={(event) => setShowEmptyDays((event.target as HTMLInputElement).checked)}
            />
          </label>

          <div class="day-groups">
            {visibleDays.map((date) => {
              const items = byDate.get(date.toString()) ?? [];
              return (
                <div key={date.toString()} class="day-group">
                  <h3 class="day-heading">{dayLabel(date, props.today)}</h3>
                  {items.length === 0 ? (
                    <p class="muted quiet-row">Nothing scheduled</p>
                  ) : (
                    <ul class="rows">
                      {items.map((occurrence) => (
                        <OccurrenceRow
                          key={occurrence.key}
                          occurrence={occurrence}
                          color={colorBySeries.get(occurrence.seriesId)}
                          locations={schedule.data?.locations ?? []}
                          onSelect={() => setSelectedKey(String(occurrence.key))}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {selected !== undefined ? (
        <OccurrenceSheet
          occurrence={selected}
          locations={schedule.data?.locations ?? []}
          override={schedule.data?.overrides.find(
            (candidate) => String(candidate.occurrenceKey) === selectedKey,
          )}
          onChanged={() => schedule.reload()}
          onEditSeries={() => {
            setSelectedKey(null);
            props.onEditSeries(selected.seriesId);
          }}
          onClose={() => setSelectedKey(null)}
        />
      ) : null}
    </>
  );
}

function MonthSchedule(props: {
  today: Temporal.PlainDate;
  initialMonth: Temporal.PlainYearMonth;
  onEditSeries: (seriesId: string) => void;
}): JSX.Element {
  const [cursor, setCursor] = useState<Temporal.PlainYearMonth>(props.initialMonth);
  const [selectedDate, setSelectedDate] = useState<string>(props.today.toString());

  const grid = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const schedule = useSchedule(grid.rangeStart, grid.rangeEnd);
  const occurrences = schedule.data?.occurrences ?? [];
  const { selectedKey, setSelectedKey, selected } = useSelection(occurrences);

  const byDate = useMemo(() => groupByDate(occurrences), [occurrences]);
  const colorBySeries = new Map((schedule.data?.series ?? []).map((item) => [item.id, item.color]));

  const selectedItems = byDate.get(selectedDate) ?? [];

  return (
    <>
      <div class="month-bar">
        <button
          type="button"
          class="icon-button"
          aria-label="Previous month"
          onClick={() => setCursor(cursor.subtract({ months: 1 }))}
        >
          ‹
        </button>
        <h3>{formatYearMonth(cursor)}</h3>
        <button
          type="button"
          class="icon-button"
          aria-label="Next month"
          onClick={() => setCursor(cursor.add({ months: 1 }))}
        >
          ›
        </button>
      </div>

      <ErrorBanner error={schedule.error} />
      <UnresolvedWarning count={schedule.data?.unresolvedSeriesIds.length ?? 0} />

      <table class="calendar">
        <caption class="visually-hidden">Classes in {formatYearMonth(cursor)}</caption>
        <thead>
          <tr>
            {grid.weekdayOrder.map((day: Weekday) => (
              <th key={day} scope="col">
                {weekdayShort(day).slice(0, 1)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.weeks.map((week) => (
            <tr key={week[0]?.toString()}>
              {week.map((date) => {
                const iso = date.toString();
                const items = byDate.get(iso) ?? [];
                const classes = [
                  'day',
                  date.month === cursor.month ? '' : 'outside',
                  iso === props.today.toString() ? 'today' : '',
                  iso === selectedDate ? 'chosen' : '',
                ]
                  .filter((name) => name.length > 0)
                  .join(' ');
                return (
                  <td key={iso} class={classes}>
                    <button
                      type="button"
                      class="day-button"
                      // Dots alone would leave the count invisible to a screen reader.
                      aria-label={`${formatDayStamp(date)}, ${items.length} ${
                        items.length === 1 ? 'class' : 'classes'
                      }`}
                      onClick={() => setSelectedDate(iso)}
                    >
                      <span class="day-number">{date.day}</span>
                      <span class="dots">
                        {items.slice(0, 4).map((occurrence) => (
                          <span
                            key={occurrence.key}
                            class="dot"
                            style={{
                              background: colorBySeries.get(occurrence.seriesId) ?? 'var(--accent)',
                            }}
                          />
                        ))}
                      </span>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div class="day-group">
        <h3 class="day-heading">{dayLabel(Temporal.PlainDate.from(selectedDate), props.today)}</h3>
        {selectedItems.length === 0 ? (
          <p class="muted quiet-row">Nothing scheduled</p>
        ) : (
          <ul class="rows">
            {selectedItems.map((occurrence) => (
              <OccurrenceRow
                key={occurrence.key}
                occurrence={occurrence}
                color={colorBySeries.get(occurrence.seriesId)}
                locations={schedule.data?.locations ?? []}
                onSelect={() => setSelectedKey(String(occurrence.key))}
              />
            ))}
          </ul>
        )}
      </div>

      {selected !== undefined ? (
        <OccurrenceSheet
          occurrence={selected}
          locations={schedule.data?.locations ?? []}
          override={schedule.data?.overrides.find(
            (candidate) => String(candidate.occurrenceKey) === selectedKey,
          )}
          onChanged={() => schedule.reload()}
          onEditSeries={() => {
            setSelectedKey(null);
            props.onEditSeries(selected.seriesId);
          }}
          onClose={() => setSelectedKey(null)}
        />
      ) : null}
    </>
  );
}

function OccurrenceRow(props: {
  occurrence: Occurrence;
  /** The class's colour; falls back for a series that predates colours. */
  color: string | undefined;
  locations: readonly Location[];
  onSelect: () => void;
}): JSX.Element {
  const { occurrence } = props;
  const cancelled = occurrence.status === 'cancelled';
  const color = props.color ?? 'var(--muted)';
  const placeName =
    props.locations.find((location) => location.id === occurrence.locationId)?.name ??
    'Unknown place';

  return (
    <li>
      <button type="button" class={`row${cancelled ? ' cancelled' : ''}`} onClick={props.onSelect}>
        <span class="row-bar" style={{ background: color }} aria-hidden="true" />
        <span class="row-when">
          <span class="row-time">{formatTime(wallClock(occurrence))}</span>
          <span class="row-length">{formatDuration(occurrence.durationMinutes)}</span>
        </span>
        <span class="row-what">
          <span class={`row-title${cancelled ? ' struck' : ''}`}>{occurrence.title}</span>
          <span class="row-place">{placeName}</span>
          {cancelled ? <span class="tag">Cancelled</span> : null}
          {occurrence.rescheduled ? <span class="tag">Moved</span> : null}
        </span>
        <ChevronRight />
      </button>
    </li>
  );
}

function UnresolvedWarning(props: { count: number }): JSX.Element | null {
  if (props.count === 0) {
    return null;
  }
  return (
    <p class="notice" role="status">
      {props.count} {props.count === 1 ? 'class is' : 'classes are'} hidden because their place was
      removed. Pick a new place for them under Classes.
    </p>
  );
}
